import { AppData, Lightning } from '@lightningjs/sdk';
import AbstractDetailsPage, {
  AbstractDetailsPageTemplateSpec,
} from './AbstractDetailsPage';
import { translate } from 'support/translate';
import { VideoLaneItem } from 'services/cwData';
import MyStuffContinueTile from 'components/common/tiles/MyStuffContinueTile';
import PortraitTile from 'components/common/tiles/PortraitTile';
import {
  SelectableTile,
  SelectableTileType,
} from 'components/common/tiles/SelectableTile';
import { Show, Video } from 'types/api/media';
import { PageId } from 'types/pageId';
import { MultipleSeriesNameTypes } from 'services/analytics/MParticle';
import { StaticViewContexts } from 'types/analytics';
import { reportMyStuffAddRemove } from 'services/analytics/reportingServicePage';

const PORTRAIT_TILE_COLUMNS = 6;
const PORTRAIT_TILE_SPACING = 20;
const PORTRAIT_TILE_CROSS_SPACING = 20;

const CONTINUE_TILE_COLUMNS = 4;
const CONTINUE_TILE_SPACING = 32;
const CONTINUE_TILE_CROSS_SPACING = 24;

export interface MyHistoryPageTemplateSpec
  extends AbstractDetailsPageTemplateSpec {
  pageData: {
    continueWatchingItems: VideoLaneItem[];
    trendingItems: (Video | Show)[];
  };
}

export default class MyHistoryPage
  extends AbstractDetailsPage<MyHistoryPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<MyHistoryPageTemplateSpec>
{
  protected override _pageId = PageId.CONTINUE_WATCHING;
  protected override _viewContext =
    StaticViewContexts.MY_STUFF_CONTINUE_WATCHING;

  private trendingItems: (Video | Show)[] = [];

  override get announce() {
    return translate('myStuff.continueWatching');
  }

  override $remove() {
    super.$remove();
    const numTilesSelected = this._ItemsList.items.filter(
      item => !!(item as SelectableTileType).isSelected,
    ).length;

    this.widgets.removeconfirmationmodal.update({
      numberOfItems: numTilesSelected,
      detailsTitle: translate('myStuff.continueWatching'),
    });
    AppData?.modalService.open('RemoveConfirmationModal');
  }

  override async $removeItems() {
    super.$removeItems();

    const idsToRemove: string[] = [];
    this._ItemsList.items.forEach(item => {
      const tile = item as SelectableTileType;
      if (tile.isSelected) {
        idsToRemove.push((tile.data as VideoLaneItem).guid || '');
      }
    });

    if (idsToRemove.length > 0) {
      await this.removeItems(idsToRemove);
      this.reportItemRemove(idsToRemove);
      await this.fetchItems();
      this.setupItemsList();

      // Toggle back edit mode
      this.$toggleEdit();
    }
  }

  override _setup() {
    super._setup();
  }

  override _onDataProvided() {
    this.loginConfig = AppData?.config?.login.logged_in;
    this.items = this.pageData?.continueWatchingItems ?? [];
    this.trendingItems = this.pageData?.trendingItems ?? [];
    this.setupItemsList();
    this.setupRemoveConfirmationModal();
  }

  private async fetchItems() {
    const continueWatchingItems =
      await AppData?.historyService.getContinueLaneItems();
    this.items = continueWatchingItems ?? [];
  }

  private setupItemsList() {
    this.loaded = false;

    if (this.items.length > 0) {
      this.showEditButton = true;
      this.title = translate('myStuff.continueWatching');
      this.subtitle = '';
    } else {
      this.showEditButton = false;
      this.title = translate('myStuff.continueWatchingEmpty');
      this.subtitle = this.loginConfig?.continue_watching?.description ?? '';
    }

    this.updateItemsList();

    this.loaded = true;
  }

  private updateItemsList() {
    const hasContinue = this.items.length > 0;
    const listItems = this.items as VideoLaneItem[];

    if (hasContinue) {
      const gridItems = listItems.map(item => ({
        type: SelectableTile(MyStuffContinueTile),
        data: item,
        imageUrl: item.images?.thumbnail || '',
      }));
      this._ItemsList.patch({
        columns: CONTINUE_TILE_COLUMNS,
        spacing: CONTINUE_TILE_SPACING,
        crossSpacing: CONTINUE_TILE_CROSS_SPACING,
        listType: SelectableTile(MyStuffContinueTile),
        items: gridItems,
      });
    } else {
      if (this.trendingItems && this.trendingItems.length > 0) {
        const gridItems = this.trendingItems.map(item => ({
          type: PortraitTile,
          data: item,
          imageUrl: item.images?.image_vertical || '',
        }));
        this._ItemsList.patch({
          columns: PORTRAIT_TILE_COLUMNS,
          spacing: PORTRAIT_TILE_SPACING,
          crossSpacing: PORTRAIT_TILE_CROSS_SPACING,
          listType: PortraitTile,
          items: gridItems,
        });
      }
    }
  }

  private reportItemRemove(idsToRemove: string[]) {
    let type: MultipleSeriesNameTypes | string;

    if (idsToRemove.length === this.items.length) {
      type = 'Select All';
    } else if (idsToRemove.length === 1) {
      const item = (this.items as VideoLaneItem[]).find(
        item => item.guid === idsToRemove[0],
      );
      type = item?.title ?? '';
    } else {
      type = 'Multiple';
    }

    reportMyStuffAddRemove({
      actionType: 'Remove',
      type,
      pageId: this.pageId,
      rowName: 'Continue Watching',
    });
  }

  private async removeItems(ids: string[]) {
    return AppData?.historyService.removeContinueWatchingItems(ids);
  }
}
